import React, {useState, useRef, useEffect} from 'react';
import {
    Paper,
    Box,
    TextField,
    Button,
    Typography,
    Avatar,
    Stack,
    Divider,
    IconButton,
    Chip,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    Grid,
    InputAdornment,
    useTheme,
    Tooltip,
    tooltipClasses,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {
    Send as SendIcon,
    SmartToy as BotIcon,
    Person as PersonIcon,
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    Psychology as PsychologyIcon,
    Save as SaveIcon,
    Preview as PreviewIcon,
    ExpandMore as ExpandMoreIcon,
    Edit as EditIcon,
    TrendingUp as TrendingUpIcon,
    Shuffle as ShuffleIcon,
    Stop as StopIcon,
    Description as DescriptionIcon,
} from '@mui/icons-material';
import {createApiClient} from '../../services/ApiClient.jsx';

// Custom wide tooltip
const WideTooltip = styled(({className, ...props}) => (
    <Tooltip {...props} classes={{popper: className}}/>
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 600,
    },
});

const AIChatInterface = ({
                             formData,
                             onUpdateFormData,
                             onRefreshFormData,
                             accessToken,
                             courseId,
                             topicId,
                         }) => {
    const theme = useTheme(); // Add theme hook
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            content: 'Hallo! 👋 Gib ein Thema ein und ich generiere passende Prüfungsfragen.',
            timestamp: new Date(),
        }
    ]);
    const [isTyping, setIsTyping] = useState(false);
    const [tempGeneratedQuestions, setTempGeneratedQuestions] = useState(null);
    const [chatContextId, setChatContextId] = useState(null);
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
    const messagesEndRef = useRef(null);

    // For background task handling
    const [isGenerating, setIsGenerating] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState(null);
    const [taskProgress, setTaskProgress] = useState(0);
    const [taskStatus, setTaskStatus] = useState(null);
    const [currentAgent, setCurrentAgent] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);

    // Document count state
    const [documentCount, setDocumentCount] = useState(null);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    const [availableDocuments, setAvailableDocuments] = useState([]);

    // Question Generator Settings
    const [difficulty, setDifficulty] = useState('mittel');
    const [questionType, setQuestionType] = useState('single_choice');
    const [numQuestions, setNumQuestions] = useState(5);

    // For mixed question types
    const [typeDistribution, setTypeDistribution] = useState({
        single_choice: 2,
        multi_choice: 1,
        text: 1,
        number: 1
    });
    const [showMixedDialog, setShowMixedDialog] = useState(false);

    const apiClient = createApiClient(accessToken);

    // Use useRef to store interval ID to avoid stale closure issues
    const pollingIntervalRef = useRef(null);

    // Helper function to get agent index for progress visualization
    const getAgentIndexFromName = (agentName) => {
        const agentOrder = ['Content Analyzer', 'Question Generator', 'Quality Validator', 'Finalizer'];
        return agentOrder.indexOf(agentName);
    };


    // Agent workflow mapping based on backend message and progress
    const getAgentFromBackendData = (backendProgress, status, backendMessage) => {
        if (status === 'pending') {
            return {
                name: 'Task Manager',
                description: 'Task in der Warteschlange...',
                icon: '⏳',
                color: 'info'
            };
        }

        if (status === 'processing') {
            // Map backend messages to agent info
            if (backendMessage?.includes('Content Analyzer')) {
                return {
                    name: 'Content Analyzer',
                    description: backendMessage,
                    icon: '🔍',
                    color: 'primary'
                };
            } else if (backendMessage?.includes('Question Generator')) {
                return {
                    name: 'Question Generator',
                    description: backendMessage,
                    icon: '🤖',
                    color: 'secondary'
                };
            } else if (backendMessage?.includes('Quality Validator')) {
                return {
                    name: 'Quality Validator',
                    description: backendMessage,
                    icon: '✅',
                    color: 'success'
                };
            } else if (backendMessage?.includes('Finalizing') || backendMessage?.includes('Processing results')) {
                return {
                    name: 'Finalizer',
                    description: backendMessage,
                    icon: '🎯',
                    color: 'success'
                };
            } else if (backendMessage?.includes('Initializing')) {
                return {
                    name: 'System',
                    description: backendMessage,
                    icon: '⚙️',
                    color: 'primary'
                };
            } else {
                // Fallback to progress-based mapping for unknown messages
                if (backendProgress < 0.25) {
                    return {
                        name: 'Content Analyzer',
                        description: backendMessage || 'Analysiert Kursinhalte und Dokumente...',
                        icon: '🔍',
                        color: 'primary'
                    };
                } else if (backendProgress < 0.75) {
                    return {
                        name: 'Question Generator',
                        description: backendMessage || 'Generiert Fragen basierend auf der Analyse...',
                        icon: '🤖',
                        color: 'secondary'
                    };
                } else if (backendProgress < 0.9) {
                    return {
                        name: 'Quality Validator',
                        description: backendMessage || 'Überprüft Qualität und Korrektheit der Fragen...',
                        icon: '✅',
                        color: 'success'
                    };
                } else {
                    return {
                        name: 'Finalizer',
                        description: backendMessage || 'Bereitet Ergebnisse vor...',
                        icon: '🎯',
                        color: 'success'
                    };
                }
            }
        }

        return {
            name: 'System',
            description: backendMessage || 'Verarbeitung läuft...',
            icon: '⚙️',
            color: 'default'
        };
    };

    // Task polling and management
    const startTaskPolling = (taskId) => {
        console.log(`Starting polling for task ${taskId}`);

        // Clear any existing polling
        stopTaskPolling();

        const pollTask = async () => {
            try {
                const response = await apiClient.get(`/api/ai/question-chat/status/${taskId}`);

                setTaskProgress(response.progress || 0);
                setTaskStatus(response.status);

                // Update current agent based on real backend data
                const agent = getAgentFromBackendData(
                    response.progress || 0,
                    response.status,
                    response.message
                );
                setCurrentAgent(agent);

                console.log(`Task ${taskId} status: ${response.status}, progress: ${response.progress}`);

                if (response.status === 'completed') {
                    // Task completed successfully
                    console.log('Task completed, stopping polling');
                    stopTaskPolling();
                    setIsGenerating(false);
                    setIsTyping(false);

                    if (response.result) {
                        handleTaskCompletion(response.result);
                    } else {
                        throw new Error('No result data received');
                    }
                } else if (response.status === 'failed') {
                    // Task failed
                    console.log('Task failed, stopping polling');
                    stopTaskPolling();
                    setIsGenerating(false);
                    setIsTyping(false);

                    const errorMessage = response.message || 'Unknown error occurred';
                    handleTaskError(errorMessage);
                }
                // If status is 'pending' or 'processing', continue polling

            } catch (error) {
                console.error('Error polling task status:', error);
                stopTaskPolling();
                setIsGenerating(false);
                setIsTyping(false);
                handleTaskError(error.message || 'Failed to check task status');
            }
        };

        // Poll every 2 seconds
        const interval = setInterval(pollTask, 2000);
        pollingIntervalRef.current = interval;
        setPollingInterval(interval); // Keep for UI state

        // Initial poll
        pollTask();
    };

    const stopTaskPolling = () => {
        if (pollingIntervalRef.current) {
            console.log('Clearing polling interval:', pollingIntervalRef.current);
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
            setPollingInterval(null);
        }
    };


    const cancelCurrentTask = async () => {
        if (currentTaskId) {
            try {
                await apiClient.delete(`/api/ai/question-chat/task/${currentTaskId}`);
                console.log(`Cancelled task ${currentTaskId}`);

                stopTaskPolling();
                setIsGenerating(false);
                setIsTyping(false);
                setCurrentTaskId(null);
                setTaskProgress(0);
                setTaskStatus(null);
                setCurrentAgent(null);

                const cancelMessage = {
                    id: Date.now(),
                    type: 'bot',
                    content: '⏹️ Task wurde erfolgreich abgebrochen.',
                    timestamp: new Date(),
                };
                setMessages(prev => [...prev, cancelMessage]);

            } catch (error) {
                console.error('Error cancelling task:', error);
                // Still stop polling and reset state
                stopTaskPolling();
                setIsGenerating(false);
                setIsTyping(false);
                setCurrentTaskId(null);
                setTaskProgress(0);
                setTaskStatus(null);
                setCurrentAgent(null);
            }
        }
    };

    const handleTaskCompletion = (result) => {
        console.log('=== TASK COMPLETION HANDLER ===');
        console.log('Task completed with result:', result);

        // Store the chat context ID
        if (result.chat_context_id) {
            setChatContextId(result.chat_context_id);
            console.log('Set chat context ID:', result.chat_context_id);
        }

        // Store the generated questions
        if (result.question) {
            const questionsWithMetadata = {
                ...result.question,
                metadata: {
                    ...result.question.metadata,
                    course_id: courseId,
                    total_questions: result.question.sections
                        ? result.question.sections.reduce((acc, s) => acc + s.questions.length, 0)
                        : 0
                }
            };
            setTempGeneratedQuestions(questionsWithMetadata);
            console.log('Set temp generated questions:', questionsWithMetadata);
        }

        // Show success message and preview
        const totalQuestions = result.question?.sections
            ? result.question.sections.reduce((acc, s) => acc + s.questions.length, 0)
            : 0;

        console.log(`Generated ${totalQuestions} questions total`);

        const successMessage = {
            id: Date.now(),
            type: 'bot',
            content: `✅ Fertig! ${totalQuestions} Fragen generiert. Prüfe die Fragen unten und klicke auf "Fragen speichern" wenn du zufrieden bist.`,
            timestamp: new Date(),
            showActions: true,
        };
        setMessages(prev => [...prev, successMessage]);

        // Show preview if questions were generated
        if (totalQuestions > 0 && result.question?.sections) {
            showQuestionPreview(result.question);
        }

        // Reset task state
        console.log('Resetting task state');
        setCurrentTaskId(null);
        setTaskProgress(0);
        setTaskStatus(null);
        setCurrentAgent(null);

        console.log('=== TASK COMPLETION HANDLER FINISHED ===');
    };

    const handleTaskError = (errorMessage) => {
        console.error('Task failed:', errorMessage);

        const errorMsg = {
            id: Date.now(),
            type: 'bot',
            content: `❌ Fehler: ${errorMessage}`,
            timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMsg]);

        // Reset task state
        setCurrentTaskId(null);
        setTaskProgress(0);
        setTaskStatus(null);
        setCurrentAgent(null);
    };

    const showQuestionPreview = (questionData) => {
        const allQuestions = questionData.sections.flatMap(s => s.questions);

        // Calculate average confidence
        let avgConfidence = 0;
        const confidenceScores = [];

        allQuestions.forEach((q) => {
            let score = 0;
            if (q.config?.confidence) {
                if (typeof q.config.confidence.score === 'number') {
                    score = q.config.confidence.score;
                } else if (typeof q.config.confidence === 'number') {
                    score = q.config.confidence;
                }

                if (score > 0 && score <= 1) {
                    score = score * 100;
                }
            }

            if (score > 0) {
                confidenceScores.push(score);
            }
        });

        if (confidenceScores.length > 0) {
            avgConfidence = confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length;
        }

        // Create preview message
        const previewParts = [`📋 **Vorschau aller ${allQuestions.length} Fragen** (Ø Qualität: ${avgConfidence.toFixed(0)}%):\n`];

        allQuestions.forEach((q, i) => {
            let questionConfidence = 0;
            if (q.config?.confidence) {
                if (typeof q.config.confidence.score === 'number') {
                    questionConfidence = q.config.confidence.score;
                } else if (typeof q.config.confidence === 'number') {
                    questionConfidence = q.config.confidence;
                }

                if (questionConfidence > 0 && questionConfidence <= 1) {
                    questionConfidence = questionConfidence * 100;
                }
            }

            const confidenceEmoji = questionConfidence >= 90 ? '🟢' :
                questionConfidence >= 80 ? '🟡' :
                    questionConfidence >= 70 ? '🟠' : '🔴';

            let preview = `${i + 1}. ${q.text} ${confidenceEmoji} ${questionConfidence.toFixed(0)}%`;

            if (q.type === 'single_choice' || q.type === 'multi_choice') {
                if (q.options && q.options.length > 0) {
                    preview += '\n' + q.options.map(opt => `   ☐ ${opt.label}`).join('\n');

                    if (q.config && q.config.correct_answer) {
                        if (q.type === 'single_choice') {
                            const correctOpt = q.options.find(opt => opt.value === q.config.correct_answer);
                            if (correctOpt) {
                                preview += `\n   ✓ Korrekt: ${correctOpt.label}`;
                            }
                        } else {
                            const correctOpts = q.options.filter(opt =>
                                q.config.correct_answer.includes(opt.value)
                            );
                            if (correctOpts.length > 0) {
                                preview += `\n   ✓ Korrekt: ${correctOpts.map(opt => opt.label).join(', ')}`;
                            }
                        }
                    }
                }
            } else if (q.config && q.config.correct_answer) {
                preview += `\n   ✓ Antwort: ${q.config.correct_answer}`;
            }

            previewParts.push(preview);
        });

        const previewMessage = {
            id: Date.now() + 1,
            type: 'bot',
            content: previewParts.join('\n\n'),
            timestamp: new Date(),
        };
        setMessages(prev => [...prev, previewMessage]);
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            console.log('Component unmounting, cleaning up polling');
            stopTaskPolling();
        };
    }, []);

    // Fetch document matrix and calculate available documents
    const fetchDocumentCount = async () => {
        if (!courseId) return;

        setIsLoadingDocuments(true);
        try {
            const response = await apiClient.get(`/api/documents/matrix/${courseId}`);

            if (response && response.documents) {
                let availableCount = 0;
                const availableDocInfo = [];

                // Get documents available to all topics
                if (response.available_to_all_topics && response.available_to_all_topics.document_ids) {
                    const allTopicDocs = response.documents.filter(doc =>
                        response.available_to_all_topics.document_ids.includes(doc.document_id)
                    );
                    availableCount += allTopicDocs.length;
                    availableDocInfo.push(...allTopicDocs.map(doc =>
                        doc.document_name || doc.original_filename
                    ));
                }

                // Get topic-specific documents if topicId is provided
                if (topicId && response.documents) {
                    const topicSpecificDocs = response.documents.filter(doc =>
                        doc.current_topic_ids && doc.current_topic_ids.includes(topicId)
                    );
                    availableCount += topicSpecificDocs.length;
                    availableDocInfo.push(...topicSpecificDocs.map(doc =>
                        doc.document_name || doc.original_filename
                    ));
                }

                setDocumentCount(availableCount);
                setAvailableDocuments(availableDocInfo);
            }
        } catch (error) {
            console.error('Error fetching document matrix:', error);
            setDocumentCount(0);
            setAvailableDocuments([]);
        } finally {
            setIsLoadingDocuments(false);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Fetch document count when courseId or topicId changes
    useEffect(() => {
        fetchDocumentCount();
    }, [courseId, topicId]);

    // Format tooltip content for available documents
    const formatDocumentTooltip = () => {
        if (!availableDocuments || availableDocuments.length === 0) {
            return "Keine Dokumente verfügbar";
        }

        return (
            <Box>
                <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                    Verfügbare Dokumente ({availableDocuments.length}):
                </Typography>
                <Stack spacing={0.5}>
                    {availableDocuments.map((docName, index) => (
                        <Typography key={index} variant="body2">
                            📄 {docName}
                        </Typography>
                    ))}
                </Stack>
            </Box>
        );
    };

    // Calculate total questions for mixed types
    const calculateTotalMixedQuestions = () => {
        return Object.values(typeDistribution).reduce((sum, val) => sum + val, 0);
    };

    // Update distribution
    const updateTypeDistribution = (type, value) => {
        const newValue = Math.max(0, parseInt(value) || 0);
        setTypeDistribution(prev => ({
            ...prev,
            [type]: newValue
        }));
    };

    const startQuestionGeneration = async (topic, isModification = false) => {
        try {
            let response;

            const requestData = {
                topic: topic,
                course_id: courseId,
                topic_id: topicId,
                difficulty: difficulty,
                question_type: questionType,
                num_questions: questionType === 'mixed' ? calculateTotalMixedQuestions() : numQuestions,
                chat_context_id: chatContextId
            };

            // Add type_distribution for mixed questions
            if (questionType === 'mixed') {
                requestData.type_distribution = typeDistribution;
            }

            if (isModification && chatContextId && tempGeneratedQuestions) {
                // Modify existing questions
                const modificationRequest = {
                    chat_context_id: chatContextId,
                    user_message: topic,
                    current_questions: tempGeneratedQuestions,
                    modification_type: "general",
                    course_id: courseId
                };

                if (topicId) {
                    const questionsWithTopicId = {
                        ...tempGeneratedQuestions,
                        metadata: {
                            ...tempGeneratedQuestions.metadata,
                            topic_id: topicId,
                            course_id: courseId
                        }
                    };
                    modificationRequest.current_questions = questionsWithTopicId;
                }

                response = await apiClient.post('/api/ai/question-modify', modificationRequest);
            } else {
                // Generate new questions
                response = await apiClient.post('/api/ai/question-chat', requestData);
            }

            // API now returns task info instead of direct results
            if (response.task_id) {
                console.log(`Started task ${response.task_id}`);
                setCurrentTaskId(response.task_id);
                startTaskPolling(response.task_id);
                return response;
            } else {
                throw new Error('No task ID received from server');
            }

        } catch (error) {
            console.error('Error starting question generation:', error);
            throw error;
        }
    };

    const formatAnswerInfo = (answer) => {
        const parts = [answer.description];

        // Nur wichtige Infos zeigen
        if (answer.quality_metrics && answer.quality_metrics.average_confidence) {
            const confidence = answer.quality_metrics.average_confidence;
            const emoji = confidence >= 80 ? '✅' : confidence >= 60 ? '⚠️' : '❌';
            parts.push(`${emoji} Qualität: ${confidence.toFixed(0)}%`);
        }

        return parts.join('\n');
    };

    // Neue Funktion für Markdown-Rendering
    const renderMessageContent = (content) => {
        // Einfaches Parsing für **fett** Text
        const parts = content.split(/(\*\*[^*]+\*\*)/g);

        return parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={index}>{part.slice(2, -2)}</strong>;
            }
            return <span key={index}>{part}</span>;
        });
    };

    // Stop function for new task-based approach
    const handleStopGeneration = async () => {
        if (currentTaskId) {
            await cancelCurrentTask();
        } else {
            // Fallback for when task hasn't started yet
            setIsGenerating(false);
            setIsTyping(false);

            const stopMessage = {
                id: Date.now(),
                type: 'bot',
                content: '⏹️ Generierung wurde gestoppt.',
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, stopMessage]);
        }
    };

    const handleSendMessage = async () => {
        if (!message.trim()) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: message.trim(),
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        const userInput = message.trim();
        setMessage('');
        setIsTyping(true);
        setIsGenerating(true);

        // Reset task state
        setTaskProgress(0);
        setTaskStatus('pending');

        // Set initial agent
        const initialAgent = getAgentFromBackendData(0, 'pending', 'Task queued for processing');
        setCurrentAgent(initialAgent);

        try {
            const isModification = chatContextId && tempGeneratedQuestions;

            const numQuestionsToGenerate = questionType === 'mixed' ?
                calculateTotalMixedQuestions() : numQuestions;

            // Show initial thinking message
            const botThinkingMessage = {
                id: Date.now() + 1,
                type: 'bot',
                content: isModification
                    ? `🤖 Starte Fragenbearbeitung...`
                    : questionType === 'mixed'
                        ? `🤖 Starte Generierung von ${numQuestionsToGenerate} gemischten Fragen...`
                        : `🤖 Starte Generierung von ${numQuestionsToGenerate} Fragen...`,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, botThinkingMessage]);

            // Start question generation (returns task info immediately)
            await startQuestionGeneration(userInput, isModification);

            // The actual result will be handled by the polling mechanism
            // in handleTaskCompletion()

        } catch (error) {
            console.error('Error starting question generation:', error);

            const errorMessage = {
                id: Date.now() + 2,
                type: 'bot',
                content: `❌ Fehler beim Starten: ${error.message}`,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);

            // Reset state on error
            setIsTyping(false);
            setIsGenerating(false);
            setTaskProgress(0);
            setTaskStatus(null);
            setCurrentTaskId(null);
            setCurrentAgent(null);
        }
    };

    const handleSaveQuestions = async () => {
        if (!tempGeneratedQuestions) return;

        setSaveConfirmDialog(false);

        try {
            if (!formData || !formData.id) {
                // Keine Form vorhanden - zeige Warnung
                const warningMessage = {
                    id: Date.now(),
                    type: 'bot',
                    content: `⚠️ Es wurde noch keine Form erstellt. Bitte erstelle zuerst eine Form, dann können die Fragen hinzugefügt werden.`,
                    timestamp: new Date(),
                };
                setMessages(prev => [...prev, warningMessage]);
                return;
            }

            // Merge mit bestehender Form
            const existingSections = formData.sections || [];
            const newSections = tempGeneratedQuestions.sections || [];

            // Stelle sicher, dass Order-Werte eindeutig sind
            let maxOrder = existingSections.reduce((max, s) => Math.max(max, s.order || 0), -1);

            const adjustedNewSections = newSections.map((section, idx) => ({
                ...section,
                order: maxOrder + idx + 1
            }));

            const mergedFormData = {
                ...formData,
                sections: [...existingSections, ...adjustedNewSections],
                topic_id: topicId
            };

            await onUpdateFormData(mergedFormData);

            const successMessage = {
                id: Date.now(),
                type: 'bot',
                content: `✅ Fragen wurden in die Form übernommen!`,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, successMessage]);

            // Reset temporäre Fragen
            setTempGeneratedQuestions(null);
            setChatContextId(null);

            const infoMessage = {
                id: Date.now() + 1,
                type: 'bot',
                content: `💡 Du kannst jetzt neue Fragen zu einem anderen Thema generieren.`,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, infoMessage]);

        } catch (error) {
            const errorMessage = {
                id: Date.now(),
                type: 'bot',
                content: `❌ Fehler beim Speichern: ${error.message}`,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (timestamp) => {
        return new Intl.DateTimeFormat('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
        }).format(timestamp);
    };

    // NEU: Dialog für gemischte Fragentypen
    const MixedTypeDialog = () => (
        <Dialog
            open={showMixedDialog}
            onClose={() => setShowMixedDialog(false)}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ShuffleIcon/>
                    <Typography variant="h6">Gemischte Fragetypen konfigurieren</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Lege fest, wie viele Fragen von jedem Typ generiert werden sollen.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Single Choice"
                            type="number"
                            value={typeDistribution.single_choice}
                            onChange={(e) => updateTypeDistribution('single_choice', e.target.value)}
                            InputProps={{
                                inputProps: {min: 0},
                                endAdornment: <InputAdornment position="end">Fragen</InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Multiple Choice"
                            type="number"
                            value={typeDistribution.multi_choice}
                            onChange={(e) => updateTypeDistribution('multi_choice', e.target.value)}
                            InputProps={{
                                inputProps: {min: 0},
                                endAdornment: <InputAdornment position="end">Fragen</InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Text"
                            type="number"
                            value={typeDistribution.text}
                            onChange={(e) => updateTypeDistribution('text', e.target.value)}
                            InputProps={{
                                inputProps: {min: 0},
                                endAdornment: <InputAdornment position="end">Fragen</InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Zahl"
                            type="number"
                            value={typeDistribution.number}
                            onChange={(e) => updateTypeDistribution('number', e.target.value)}
                            InputProps={{
                                inputProps: {min: 0},
                                endAdornment: <InputAdornment position="end">Fragen</InputAdornment>
                            }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1}}>
                    <Typography variant="body1" fontWeight="bold">
                        Gesamt: {calculateTotalMixedQuestions()} Fragen
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowMixedDialog(false)}>Abbrechen</Button>
                <Button
                    variant="contained"
                    onClick={() => setShowMixedDialog(false)}
                    disabled={calculateTotalMixedQuestions() === 0}
                >
                    Übernehmen
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Preview Dialog für Fragen
    const QuestionPreviewDialog = () => (
        <Dialog
            open={showPreviewDialog}
            onClose={() => setShowPreviewDialog(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <PreviewIcon/>
                    <Typography variant="h6">Fragen-Vorschau</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {tempGeneratedQuestions?.sections?.map((section, sIdx) => (
                    <Accordion key={sIdx} defaultExpanded={sIdx === 0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                            <Typography>{section.title} ({section.questions.length} Fragen)</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                {section.questions.map((q, qIdx) => (
                                    <Box key={qIdx} sx={{
                                        p: 2,
                                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                                        borderRadius: 1
                                    }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                                <Typography variant="body1" fontWeight="bold">
                                                    {qIdx + 1}. {q.text}
                                                </Typography>
                                                {q.config?.confidence && (
                                                    <Chip
                                                        size="small"
                                                        icon={<TrendingUpIcon/>}
                                                        label={`${q.config.confidence.score.toFixed(0)}%`}
                                                        color={q.config.confidence.score >= 80 ? 'success' : 'warning'}
                                                    />
                                                )}
                                            </Stack>

                                            {/* Show all options for choice questions */}
                                            {(q.type === 'single_choice' || q.type === 'multi_choice') && q.options && (
                                                <Box sx={{ml: 2}}>
                                                    {q.options.map((opt, oIdx) => {
                                                        const isCorrect = q.type === 'single_choice'
                                                            ? opt.value === q.config?.correct_answer
                                                            : q.config?.correct_answer?.includes(opt.value);

                                                        return (
                                                            <Stack key={oIdx} direction="row" alignItems="center"
                                                                   spacing={1}>
                                                                <Typography
                                                                    variant="body2"
                                                                    color={isCorrect ? 'success.main' : 'text.secondary'}
                                                                    fontWeight={isCorrect ? 'bold' : 'normal'}
                                                                >
                                                                    {isCorrect ? '✓' : '○'} {opt.label}
                                                                </Typography>
                                                            </Stack>
                                                        );
                                                    })}
                                                </Box>
                                            )}

                                            {/* Show answer for other question types */}
                                            {q.type !== 'single_choice' && q.type !== 'multi_choice' && q.config?.correct_answer && (
                                                <Box sx={{ml: 2}}>
                                                    <Typography variant="body2" color="success.main">
                                                        ✓ Antwort: {q.config.correct_answer}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Show additional info */}
                                            <Stack direction="row" spacing={2} sx={{mt: 1}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Typ: {q.type.replace('_', ' ')}
                                                </Typography>
                                                {q.cognitive_level && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Niveau: {q.cognitive_level}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowPreviewDialog(false)}>Schließen</Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        setShowPreviewDialog(false);
                        setSaveConfirmDialog(true);
                    }}
                    startIcon={<SaveIcon/>}
                >
                    Fragen speichern
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Paper
            sx={{
                height: {xs: 'calc(100vh - 200px)', md: 'calc(100vh - 250px)'}, // Responsive height
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '800px', // Max height for very large screens
                minHeight: '400px', // Min height for very small screens
            }}
            variant="outlined"
        >
            {/* Chat Header */}
            <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0}}>
                <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{bgcolor: 'primary.main'}}>
                            <PsychologyIcon/>
                        </Avatar>
                        <Box>
                            <Typography variant="h6">KI Fragen-Assistent</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Schnell • Präzise • Anpassbar
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Settings */}
                    <Stack direction="row" spacing={1} sx={{flexWrap: 'wrap', gap: 1}}>
                        <FormControl size="small" sx={{minWidth: 120}}>
                            <InputLabel>Schwierigkeit</InputLabel>
                            <Select
                                value={difficulty}
                                label="Schwierigkeit"
                                onChange={(e) => setDifficulty(e.target.value)}
                            >
                                <MenuItem value="leicht">Leicht</MenuItem>
                                <MenuItem value="mittel">Mittel</MenuItem>
                                <MenuItem value="schwer">Schwer</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{minWidth: 140}}>
                            <InputLabel>Fragetyp</InputLabel>
                            <Select
                                value={questionType}
                                label="Fragetyp"
                                onChange={(e) => setQuestionType(e.target.value)}
                            >
                                <MenuItem value="single_choice">Single Choice</MenuItem>
                                <MenuItem value="multi_choice">Multiple Choice</MenuItem>
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Zahl</MenuItem>
                                <MenuItem value="mixed">
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <ShuffleIcon fontSize="small"/>
                                        <span>Gemischt</span>
                                    </Stack>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        {questionType === 'mixed' ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setShowMixedDialog(true)}
                                startIcon={<EditIcon/>}
                            >
                                {calculateTotalMixedQuestions()} Fragen
                            </Button>
                        ) : (
                            <FormControl size="small" sx={{minWidth: 80}}>
                                <InputLabel>Anzahl</InputLabel>
                                <Select
                                    value={numQuestions}
                                    label="Anzahl"
                                    onChange={(e) => setNumQuestions(e.target.value)}
                                >
                                    {[2, 3, 5, 10, 15, 20].map(n => (
                                        <MenuItem key={n} value={n}>{n}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                </Stack>
            </Box>

            {/* Action Bar for generated questions */}
            {tempGeneratedQuestions && (
                <Box
                    sx={{
                        p: 2.5,
                        borderLeft: 4,
                        borderLeftColor: 'success.main',
                        borderBottom: 1,
                        borderBottomColor: 'divider',
                        flexShrink: 0
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <CheckCircleIcon sx={{color: 'success.main', mr: 1}}/>
                        <Typography
                            variant="body1"
                            sx={{
                                flexGrow: 1,
                                fontWeight: 500,
                                color: 'text.primary'
                            }}
                        >
                            Fragen wurden generiert und können bearbeitet werden
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            startIcon={<PreviewIcon/>}
                            onClick={() => setShowPreviewDialog(true)}
                            sx={{
                                color: 'text.primary',
                                '&:hover': {backgroundColor: 'action.hover'}
                            }}
                        >
                            Vorschau
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon/>}
                            onClick={() => setSaveConfirmDialog(true)}
                            sx={{
                                backgroundColor: 'success.main',
                                '&:hover': {backgroundColor: 'success.dark'}
                            }}
                        >
                            Speichern
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* Messages Area - This is the scrollable part - FIXED BACKGROUND */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
                minHeight: 0, // Important for flex child to shrink
            }}>
                {messages.map((msg) => (
                    <Box
                        key={msg.id}
                        sx={{
                            display: 'flex',
                            justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                            mb: 2,
                        }}
                    >
                        <Stack
                            direction={msg.type === 'user' ? 'row-reverse' : 'row'}
                            spacing={1}
                            alignItems="flex-start"
                            sx={{maxWidth: '85%'}}
                        >
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: msg.type === 'user' ? 'secondary.main' : 'primary.main',
                                }}
                            >
                                {msg.type === 'user' ? <PersonIcon/> : <PsychologyIcon/>}
                            </Avatar>
                            <Paper
                                sx={{
                                    p: 2,
                                    bgcolor: msg.type === 'user' ? 'primary.light' : 'background.paper',
                                    borderRadius: 2,
                                    boxShadow: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    component="div"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {renderMessageContent(msg.content)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block',
                                        mt: 1,
                                        opacity: 0.7,
                                        fontSize: '0.7rem',
                                    }}
                                >
                                    {formatTime(msg.timestamp)}
                                </Typography>
                            </Paper>
                        </Stack>
                    </Box>
                ))}

                {/* Agent Status Indicator - FIXED BACKGROUND */}
                {isTyping && currentAgent && (
                    <Box sx={{display: 'flex', justifyContent: 'flex-start', mb: 2}}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Avatar sx={{width: 32, height: 32, bgcolor: 'primary.main'}}>
                                <PsychologyIcon/>
                            </Avatar>
                            <Paper sx={{p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, minWidth: 350}}>
                                <Stack spacing={2}>
                                    <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Typography variant="h4" sx={{fontSize: '1.5rem'}}>
                                                {currentAgent.icon}
                                            </Typography>
                                            <Stack>
                                                <Typography variant="body1" color="text.primary" fontWeight="bold">
                                                    {currentAgent.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {currentAgent.description}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={handleStopGeneration}
                                            startIcon={<StopIcon/>}
                                        >
                                            Stoppen
                                        </Button>
                                    </Stack>

                                    {/* Agent Progress Visualization */}
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {['Content Analyzer', 'Question Generator', 'Quality Validator', 'Finalizer'].map((agent, index) => {
                                                const isActive = currentAgent.name === agent;
                                                const isCompleted = getAgentIndexFromName(currentAgent.name) > index;

                                                return (
                                                    <React.Fragment key={agent}>
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '50%',
                                                                bgcolor: isCompleted ? 'success.main' :
                                                                        isActive ? 'primary.main' : 'grey.300',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        />
                                                        {index < 3 && (
                                                            <Box
                                                                sx={{
                                                                    width: 20,
                                                                    height: 2,
                                                                    bgcolor: isCompleted ? 'success.main' : 'grey.300',
                                                                    transition: 'all 0.3s ease'
                                                                }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </Stack>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Typography variant="caption" color="text.secondary">
                                                Fortschritt: {Math.round(taskProgress * 100)}%
                                            </Typography>
                                            {currentTaskId && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Task: {currentTaskId.slice(0, 8)}...
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Box>
                )}

                <div ref={messagesEndRef}/>
            </Box>

            {/* Input Area - FIXED BACKGROUND */}
            <Box sx={{p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0}}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder={
                            tempGeneratedQuestions
                                ? "Schreibe Änderungswünsche oder ein neues Thema..."
                                : "Gib ein Thema ein, zu dem Fragen generiert werden sollen..."
                        }
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        variant="outlined"
                        size="small"
                        disabled={isTyping || isGenerating}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!message.trim() || isTyping || isGenerating}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                            },
                            '&:disabled': {
                                bgcolor: 'grey.300',
                                color: 'grey.500',
                            },
                        }}
                    >
                        <SendIcon/>
                    </IconButton>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
                    Enter zum Senden • Die KI generiert Fragen mit Confidence-Scores
                </Typography>

                {/* Status indicator */}
                <Stack direction="row" spacing={1} sx={{mt: 1}}>
                    {courseId && (
                        <WideTooltip
                            title={formatDocumentTooltip()}
                            placement="top"
                            arrow
                            enterDelay={500}
                            leaveDelay={200}
                        >
                            <Chip
                                icon={<DescriptionIcon/>}
                                label={
                                    isLoadingDocuments
                                        ? "Lade Dokumente..."
                                        : documentCount !== null
                                            ? `${documentCount} Dokument${documentCount !== 1 ? 'e' : ''} verfügbar`
                                            : "Keine Dokumente"
                                }
                                size="small"
                                variant="outlined"
                                color={documentCount > 0 ? "success" : "default"}
                                sx={{cursor: documentCount > 0 ? 'help' : 'default'}}
                            />
                        </WideTooltip>
                    )}
                    {tempGeneratedQuestions && (
                        <Chip
                            icon={<EditIcon/>}
                            label="Fragen können bearbeitet werden"
                            size="small"
                            variant="outlined"
                            color="info"
                        />
                    )}
                </Stack>
            </Box>

            {/* Dialogs */}
            <MixedTypeDialog/>
            <QuestionPreviewDialog/>

            {/* Save Confirm Dialog */}
            <Dialog
                open={saveConfirmDialog}
                onClose={() => setSaveConfirmDialog(false)}
            >
                <DialogTitle>Fragen speichern?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Die generierten Fragen in die Form übernehmen?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveConfirmDialog(false)}>Abbrechen</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveQuestions}
                        startIcon={<SaveIcon/>}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AIChatInterface;